// Shared pieces for Reports → Clients (Starchive-Client-Accounts-Plan section 3; mockup "Starchive Client Profiles").
import type { ReactNode } from 'react'
import { money } from '../../format'

export interface ClientRow {
  id: string; name: string; email: string; phone: string; city: string; instagram: string
  tier: 'top' | 'core' | 'buyer' | 'prospect' | null; pinned: boolean; advisor: string | null; advisor_name: string | null
  life: number; l12: number; purchases: number; avg: number; first: string | null; last: string | null; days: number | null
  split: [number, number, number]; counter: number; email_ok: boolean; sms_ok: boolean
  seller: boolean; consignor: boolean; consignor_key: string | null; sold: number; sold_n: number; consigned_n: number; paid_out: number; last_intake: string | null
  lapsed: boolean; last_outreach: string | null; notes_n: number; internal: boolean; stub: boolean
}
export interface Advisor { employee_id: string; name: string; owner: boolean }

export const TIER_LABEL: Record<string, string> = { top: 'Top', core: 'Core', buyer: 'Buyer', prospect: 'Prospect' }
export function TierChip({ tier, pinned }: { tier: string | null; pinned?: boolean }) {
  if (!tier) return <span className="cl-tier cl-tier--none">No tier</span>
  return <span className={'cl-tier cl-tier--' + tier} title={pinned ? 'Pinned by an owner' : 'By the tier rule'}>{TIER_LABEL[tier] ?? tier}{pinned && <svg width="10" height="10" viewBox="0 0 24 24" aria-label="pinned" fill="currentColor"><path d="M14 2l8 8-3 1-4 4 1 5-2 2-5-5-5 5-1-1 5-5-5-5 2-2 5 1 4-4z" /></svg>}</span>
}

const initials = (name: string) => name.split(/\s+/).filter(Boolean).map((s) => s[0]).join('').slice(0, 2).toUpperCase()
export function Avatar({ name, size = 26 }: { name: string | null; size?: number }) {
  if (!name) return <span className="cl-av cl-av--none" style={{ width: size, height: size }} title="No advisor">?</span>
  return <span className="cl-av" style={{ width: size, height: size, fontSize: size * 0.4 }} title={name}>{initials(name)}</span>
}

/** In person / online / arranged by staff, as one thin bar. */
export function SplitBar({ split, height = 6 }: { split: [number, number, number]; height?: number }) {
  const t = split[0] + split[1] + split[2]
  if (!t) return <span className="cl-split cl-split--empty" style={{ height }} />
  const label = `In person ${money(split[0], true)}, online ${money(split[1], true)}, arranged by staff ${money(split[2], true)}`
  return (
    <span className="cl-split" style={{ height }} title={label} aria-label={label} role="img">
      {split.map((v, i) => v > 0 ? <i key={i} className={'cl-split__' + ['ip', 'on', 'st'][i]} style={{ flexBasis: `${(100 * v) / t}%` }} /> : null)}
    </span>
  )
}
export function SplitKey() {
  return <span className="cl-key"><span><i className="cl-split__ip" />In person</span><span><i className="cl-split__on" />Online</span><span><i className="cl-split__st" />Arranged by staff</span></span>
}

export const Dot = ({ on, label }: { on: boolean; label: string }) => <span className={'cl-dot' + (on ? ' is-on' : '')} title={`${label}: ${on ? 'yes' : 'no'}`} aria-label={`${label}: ${on ? 'yes' : 'no'}`} />

export function Seg<T extends string>({ value, options, onChange, label }: { value: T; options: [T, ReactNode][]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="cl-seg" role="group" aria-label={label}>
      {options.map(([k, l]) => <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}>{l}</button>)}
    </div>
  )
}

export const CHANNEL_LABEL: Record<string, string> = { in_person: 'In person', online: 'Online', staff: 'Arranged by staff' }
const TENDERS: Record<string, string> = { card: 'Card', cash: 'Cash', zelle: 'Zelle', crypto: 'Crypto', paypal: 'PayPal', afterpay: 'Afterpay', trade: 'Trade', wire: 'Wire', trade_credit: 'Trade-in credit', manual: 'Manual', split: 'Split', none: 'No payment' }
export const tenderLabel = (t: unknown) => String(t ?? '').split('+').filter(Boolean).map((x) => TENDERS[x] ?? x).join(' + ')
export const KIND_LABEL: Record<string, string> = { purchase: 'Bought', sold_to_us: 'Sold to Starchive', traded_in: 'Traded in', consigned: 'Consigned', payout: 'Paid out', trade: 'Trade' }

export function daysLabel(days: number | null) {
  if (days == null) return '—'
  if (days <= 0) return 'Today'
  if (days === 1) return '1 day'
  return `${days.toLocaleString('en-US')} days`
}

/** Client rows to a CSV download (Top clients, Lapsed, Sellers). */
export function clientsCsv(rows: ClientRow[], name: string) {
  const cols: [string, (r: ClientRow) => unknown][] = [
    ['name', (r) => r.name], ['tier', (r) => r.tier ?? ''], ['pinned', (r) => r.pinned ? 'yes' : ''], ['advisor', (r) => r.advisor_name ?? ''],
    ['lifetime', (r) => r.life], ['last_12_months', (r) => r.l12], ['purchases', (r) => r.purchases], ['average', (r) => r.avg],
    ['in_person', (r) => r.split[0]], ['online', (r) => r.split[1]], ['arranged_by_staff', (r) => r.split[2]], ['counter_only', (r) => r.counter],
    ['first_purchase', (r) => (r.first ?? '').slice(0, 10)], ['last_purchase', (r) => (r.last ?? '').slice(0, 10)], ['days_since', (r) => r.days ?? ''],
    ['email', (r) => r.email], ['phone', (r) => r.phone], ['city', (r) => r.city], ['email_marketing', (r) => r.email_ok ? 'yes' : 'no'], ['sms_marketing', (r) => r.sms_ok ? 'yes' : 'no'],
    ['sold_to_starchive', (r) => r.sold], ['consignor', (r) => r.consignor_key ?? ''], ['last_outreach', (r) => (r.last_outreach ?? '').slice(0, 10)],
  ]
  const cell = (v: unknown) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
  const csv = '﻿' + [cols.map((c) => c[0]).join(','), ...rows.map((r) => cols.map(([, f]) => cell(f(r))).join(','))].join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** "20 Sep 2026", in New York time. */
export function dateY(v: unknown) {
  if (!v) return '—'
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(d).map((x) => [x.type, x.value]))
  return `${p.day} ${MON[Number(p.month) - 1]} ${p.year}`
}
